import { useSelector } from "react-redux";
import { OrderEvent } from "@app/domain";
import { Card } from "@app/design-system/components";
import { EventIcon } from "@app/design-system/icons";
import { CustomFieldsNavButton } from "@app/features/shared/CustomFieldsNavButton";
import { formatDateTime } from "@app/shared/utils/formatDate";
import { cx } from "@app/shared/utils/cx";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import styles from "./LastEventCard.module.css";

export type LastEventCardProps = {
  event: OrderEvent;
  /** Lets a host (e.g. Home's BottomPanel, which fuses this card flush into
   * a larger panel on mobile/tablet) override the default rounded/shadowed
   * look without forking the component — mirrors `DriverCard`'s `className`. */
  className?: string;
  /** Order Details stacks this alongside its other sections as one grouped
   * list — see `Card`'s `grouped` prop. BottomPanel doesn't pass this; it
   * already fuses this card flush via its own `className` override. */
  grouped?: boolean;
  /** Order Details' non-Ondemand section extends this card with the same
   * Customer/Driver custom-fields navigation pattern — the badge + arrow
   * (see `CustomFieldsNavButton`) is the only clickable part of the card,
   * navigating to the Event List page. Omit both to get the plain card
   * Home's BottomPanel uses. */
  eventsCount?: number;
  onEventsClick?: () => void;
};

/** Non-Ondemand counterpart to `DriverCard` — shown in the same slot of
 * Home's bottom panel when the order has no driver section to render (see
 * `BottomPanel.tsx`), and in Order Details' Driver section slot for
 * non-Ondemand orders (see `OrderDetailsPage.tsx`). */
export const LastEventCard = ({ event, className, grouped, eventsCount, onEventsClick }: LastEventCardProps) => {
  const language = useSelector((state: RootState) => state.setting.language);
  const hasEventsNav = !!eventsCount && eventsCount > 0 && !!onEventsClick;

  return (
    <Card grouped={grouped} className={cx(styles.card, className)}>
      <EventIcon size={32} color="var(--color-text-secondary)" className={styles.icon} />
      <div className={styles.body}>
        <div className={styles.title}>{t("LastEvent", language)}</div>
        <div className={styles.description}>{event.orderStatusDescription}</div>
      </div>
      <span className={styles.time}>{formatDateTime(event.occurredAt, language)}</span>
      {hasEventsNav ? (
        <CustomFieldsNavButton
          count={eventsCount!}
          onClick={onEventsClick!}
          ariaLabel={`${t("ViewEvents", language)} (${eventsCount})`}
        />
      ) : null}
    </Card>
  );
};
